import getTimezones from "@/services/timezone-service.ts";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import {
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleTimezoneSource,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { Select, Stack, Text } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, FieldPath, FieldValues, PathValue, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface WorkflowTriggerTimingFormFields {
  day_type: ApiWorkflowTriggerScheduleDayType;
  trigger_hour_local: number;
  timezone_source: ApiWorkflowTriggerScheduleTimezoneSource;
  timezone_name: string | null;
}

interface WorkflowTriggerTimingEditorProps<
  TFieldValues extends FieldValues & WorkflowTriggerTimingFormFields,
> {
  form: UseFormReturn<TFieldValues>;
}

export const WorkflowTriggerTimingEditor = <
  TFieldValues extends FieldValues & WorkflowTriggerTimingFormFields,
>({
  form,
}: WorkflowTriggerTimingEditorProps<TFieldValues>) => {
  const { t } = useTranslation();
  const {
    getWorkflowTriggerScheduleDayTypeOptions,
    getWorkflowTriggerScheduleTimezoneSourceOptions,
  } = useWorkflows();
  const hourOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, hour) => ({
        id: hour,
        title: `${hour.toString().padStart(2, "0")}:00`,
      })),
    []
  );
  const timezoneOptions = useMemo(() => getTimezones(), []);
  const timezoneSource = form.watch(
    "timezone_source" as FieldPath<TFieldValues>
  ) as ApiWorkflowTriggerScheduleTimezoneSource;

  return (
    <Stack>
      <Controller
        control={form.control}
        name={"day_type" as FieldPath<TFieldValues>}
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("workflow:trigger_schedule_day_type")}
            emptyTitle={t("workflow:trigger_schedule_day_type")}
            value={value as ApiWorkflowTriggerScheduleDayType}
            options={getWorkflowTriggerScheduleDayTypeOptions()}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={form.control}
        name={"trigger_hour_local" as FieldPath<TFieldValues>}
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("workflow:trigger_schedule_hour")}
            emptyTitle={t("workflow:trigger_schedule_hour")}
            value={value as number}
            options={hourOptions}
            onChange={(nextValue) => onChange(Number(nextValue))}
          />
        )}
      />
      <Controller
        control={form.control}
        name={"timezone_source" as FieldPath<TFieldValues>}
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("common:timezone")}
            emptyTitle={t("common:timezone")}
            value={value as ApiWorkflowTriggerScheduleTimezoneSource}
            options={getWorkflowTriggerScheduleTimezoneSourceOptions()}
            onChange={(nextValue) => {
              onChange(nextValue);
              if (nextValue !== ApiWorkflowTriggerScheduleTimezoneSource.custom) {
                form.setValue(
                  "timezone_name" as FieldPath<TFieldValues>,
                  null as PathValue<TFieldValues, FieldPath<TFieldValues>>,
                  { shouldDirty: true, shouldValidate: true }
                );
              }
            }}
          />
        )}
      />
      {timezoneSource === ApiWorkflowTriggerScheduleTimezoneSource.custom && (
        <Controller
          control={form.control}
          name={"timezone_name" as FieldPath<TFieldValues>}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              label={t("common:timezone")}
              emptyTitle={t("common:select")}
              value={value as string | null}
              options={timezoneOptions}
              onChange={onChange}
              searchable
              error={error?.message}
            />
          )}
        />
      )}
      <Text className={"text-secondary text-sm py-1"}>
        {t("workflow:trigger_schedule_timezone_hint")}
      </Text>
    </Stack>
  );
};
